<template>
  <a-modal
    :visible="visible"
    :maskClosable="false"
    :title="$t(isUpload ? 'attach_form_attach_upload' : 'attach_form_attach_edit')"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :confirmLoading="loading"
    width="50%"
    centered
    destroyOnClose
  >
    <a-form-model
      ref="attachForm"
      :model="formData"
      :rules="rules"
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 16 }"
    >
      <!-- 上传模式才允许修改文件夹 -->
      <a-form-model-item :label="$t('attach_form_folder')" prop="id" v-if="isUpload">
        <a-tree-select
          v-model="formData.id"
          style="width: 100%"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="treeData"
          :placeholder="$t('attach_form_folder_placeholder')"
          tree-default-expand-all
        >
        </a-tree-select>
      </a-form-model-item>

      <a-form-model-item :label="$t('attach_form_type')" prop="type">
        <a-select
          :getPopupContainer="triggerNode => triggerNode.parentNode"
          v-model="formData.type"
          @change="typeChange"
        >
          <a-select-option
            :value="typeItem.value"
            v-for="typeItem in attachTypeList"
            :key="typeItem.value"
          >
            {{ typeItem.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <!-- 编辑模式，不允许重新上传 -->
      <a-form-model-item :label="$t('attach_form_attach_upload')" prop="files" v-if="isUpload">
        <UploadOSS
          :value="formData.files"
          :manuallyUpload="false"
          @change="uploadChange"
          :maxCount="1"
          ref="uploadFile"
          uploadDirInOSS="attachment"
          uploadType="file"
          :acceptFileSize="1024 * 1024 * 1024"
          acceptFileTypes="any"
          :uploadBucket="uploadBucket"
          :downloadFileName="downloadFileName"
        />
      </a-form-model-item>

      <!-- 名称：编辑时不允许编辑 -->
      <a-form-model-item
        :label="$t('attach_form_file_name')"
        prop="fileName"
        v-if="formData.files && formData.files.length > 0"
      >
        <a-input
          v-model="formData.fileName"
          :placeholder="$t('device_form_name_placeholder')"
          :disabled="!isUpload"
        />
      </a-form-model-item>

      <!-- 大小：编辑时不显示 -->
      <a-form-model-item
        :label="$t('attach_form_file_size')"
        prop="fileSize"
        v-if="formData.fileSize && isUpload"
        :help="filters.formatSize(formData.fileSize)"
      >
        <a-input v-model="formData.fileSize" disabled>
          <div slot="addonAfter">Bytes</div>
        </a-input>
      </a-form-model-item>

      <a-form-model-item :label="$t('attach_form_read_permission')" prop="readLevel">
        <a-select
          :getPopupContainer="triggerNode => triggerNode.parentNode"
          v-model="formData.readLevel"
          :placeholder="$t('attach_form_read_permission_placeholder')"
          allowClear
        >
          <a-select-option :value="item.value" v-for="item in readList" :key="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item :label="$t('attach_form_write_permission')" prop="writeLevel">
        <a-select
          :getPopupContainer="triggerNode => triggerNode.parentNode"
          v-model="formData.writeLevel"
          :placeholder="$t('attach_form_write_permission_placeholder')"
          allowClear
        >
          <a-select-option :value="item.value" v-for="item in writeList" :key="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item :label="$t('attach_form_file_desc')" prop="intro">
        <a-textarea
          v-model="formData.intro"
          :placeholder="$t('attach_form_file_desc_placeholder')"
          :rows="4"
        />
      </a-form-model-item>
    </a-form-model>
  </a-modal>
</template>

<script>
import attachConfig from '@/views/attach/mixins/config';
import * as API from '@/api/attach';
import { firmwareBucket } from '@/config/oss';
// import { generateRandomCode } from '@/libs/utils';

export default {
  mixins: [attachConfig],
  name: 'AttachDialog',
  props: {
    nodeData: {
      type: Object,
      default: () => {},
    },
    treeData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filters: this.$options.filters,
      visible: false,
      loading: false,
      // 是否为上传
      isUpload: false,

      fileList: [],

      formData: {
        id: undefined,
        type: 0,
        files: [],
        readLevel: 5,
        writeLevel: 5,
        intro: undefined,
        fileName: undefined,
        fileSize: undefined,
      },
      defaultFormData: {
        id: undefined,
        type: 0,
        files: [],
        readLevel: 5,
        writeLevel: 5,
        intro: undefined,
        fileName: undefined,
        fileSize: undefined,
      },
      rules: {
        files: [
          {
            required: true,
            message: this.$t('attach_form_upload_placeholder'),
            trigger: 'change',
          },
        ],
        type: [
          {
            required: true,
            message: this.$t('attach_form_type_placeholder'),
            trigger: 'change',
          },
        ],
        fileName: [
          {
            required: true,
            message: this.$t('attach_form_file_name_placeholder'),
            trigger: 'change',
          },
        ],
        id: [
          {
            required: true,
            message: this.$t('attach_form_upload_target_folder'),
            trigger: 'change',
          },
        ],
        readLevel: [
          {
            required: true,
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value && value !== 0) {
                callback(this.$t('attach_form_read_permission_placeholder'));
                return false;
              }
              if (value < this.nodeData?.readLevel) {
                callback(
                  this.$t('attach_form_min_level_limit_tips', { level: this.nodeData?.readLevel })
                );
                return false;
              }
              callback();
            },
          },
        ],
        writeLevel: [
          {
            required: true,
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value && value !== 0) {
                callback(this.$t('attach_form_write_permission_placeholder'));
                return false;
              }
              if (value < this.nodeData?.writeLevel) {
                callback(
                  this.$t('attach_form_min_level_limit_tips', {
                    level: this.nodeData?.writeLevel,
                  })
                );
                return false;
              }
              callback();
            },
          },
        ],
      },

      // 上传文件
      uploadOrignFile: null,

      uploadBucket: null,
      downloadFileName: null,
      firmwareBucket: firmwareBucket,
    };
  },
  methods: {
    // 显示上传/编辑附件弹窗
    show(attach, isUpload) {
      this.isUpload = isUpload;

      if (this.isUpload) {
        // 根目录不允许上传文件，根目录id为0
        if (this.nodeData.id !== 0) {
          this.formData.id = this.nodeData.id;
        }
      } else {
        // 编辑模式，填充表单数据
        this.formData = {
          id: attach.id,
          type: attach.type,
          files: [attach.url],
          readLevel: attach.readLevel || 5,
          writeLevel: attach.writeLevel || 5,
          intro: attach.intro,
          fileName: attach.name,
          fileSize: attach.size,
        };
      }

      this.visible = true;
    },

    // 附件类型变化
    typeChange(value) {
      if (value === 1) {
        // 固件时，固定下载文件名（设置为true，表示下载时文件名跟上传时的原文件名保持一致）
        this.downloadFileName = true;
      } else {
        this.downloadFileName = null;
      }
    },

    // 上传文件变化
    uploadChange(fileList, originFileObj) {
      if (fileList && fileList.length > 0) {
        this.formData.files = fileList || [];
        this.uploadOrignFile = originFileObj;

        // 文件名，文件大小
        const _fileName = this.uploadOrignFile[0]?.originFileObj?.name || this.uploadOrignFile.name;
        const _fileSize = this.uploadOrignFile[0]?.originFileObj?.size || this.uploadOrignFile.size;
        this.formData.fileName = _fileName;
        this.formData.fileSize = _fileSize;
      } else {
        this.formData.files = [];
        this.uploadOrignFile = undefined;
        this.formData.fileName = '';
        this.formData.fileSize = '';
      }
    },

    // 取消按钮处理
    handleCancel() {
      this.visible = false;
      this.resetForm();
    },

    // 提交表单处理
    handleSubmit() {
      this.$refs.attachForm.validate(valid => {
        if (valid) {
          this.loading = true;

          // 参数处理
          const params = { ...this.formData };

          // 重新组装files参数
          if (this.formData.files && this.formData.files.length > 0) {
            const _files = {
              fileName: this.formData.fileName,
              icon: 0,
              size: this.formData.fileSize,
              url: this.formData.files[0],
            };

            // 找到文件名对应的icon值（icon值 前端写死，map到数据保存，所以前端的icon value值不可随意更改）
            const suffix = '.' + _files.fileName.split('.').pop();
            _files.icon = this.getFileType(suffix)?.value || 0;
            params.files = [_files];
          }

          // 删除无必要参数
          delete params.fileName;
          delete params.fileSize;

          if (this.isUpload) {
            this.uploadAttach(params);
          } else {
            this.editAttach(params);
          }
        }
      });
    },

    // 编辑附件 保存
    editAttach(params) {
      API.editAttachment(params)
        .then(res => {
          if (res.code === 0) {
            this.$emit('callback', true);
            this.$message.success(this.$t('com_msg_edit_success'));
            this.handleCancel();
          }
        })
        .catch(() => {
          this.$message.error(this.$t('com_msg_edit_fail'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 上传、编辑附件 保存
    uploadAttach(params) {
      API.uploadMultipleAttachments(params)
        .then(res => {
          if (res.code === 0) {
            this.$emit('callback', true);
            this.$message.success(this.$t('attach_upload_success'));
            this.handleCancel();
          } else {
            this.$message.error(res.message || this.$t('attach_upload_failed'));
          }
        })
        .catch(() => {
          this.$message.error(this.$t('attach_upload_failed'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 重置表单
    resetForm() {
      this.$refs.attachForm.resetFields();
      this.fileList = [];
      this.formData = {
        ...this.defaultFormData,
      };
    },
  },
};
</script>
